import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VoiceErrorToast.module.css';

interface VoiceErrorToastProps {
  /** Raw error message from useSpeechToText. Matched against known
   *  patterns to pick an i18n key; unknown strings fall through to the
   *  generic "voice didn't save" copy. */
  error: string;
  onDismiss: () => void;
}

/**
 * Bottom-of-screen toast that surfaces voice-capture failures so the user
 * doesn't sit with a silent mic. Auto-dismisses after 5 seconds or on
 * tap. Positioned fixed with z-index above overlays (sheet, undo toast)
 * and below the first-tap disclosure.
 *
 * Three categories are recognized:
 *   - permission denied (browser or getUserMedia)
 *   - no speech detected (empty transcription)
 *   - generic (transcription 502, recorder errors, etc.)
 */
export default function VoiceErrorToast({ error, onDismiss }: VoiceErrorToastProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const id = setTimeout(onDismiss, 5000);
    return () => clearTimeout(id);
  }, [error, onDismiss]);

  const lower = error.toLowerCase();
  const message =
    lower.includes('permission') || lower.includes('not-allowed')
      ? t('voice.errorPermission')
      : error === 'No speech detected'
      ? t('voice.errorNoSpeech')
      : t('voice.errorGeneric');

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={onDismiss}
      role="status"
      aria-live="assertive"
    >
      <span className={styles.icon} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </span>
      <span className={styles.message}>{message}</span>
    </button>
  );
}
